import type { Registry as Services } from '@ember/service';
import { service } from '@ember/service';
import Model, {
  attr,
  belongsTo,
  type HasMany,
  hasMany,
} from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import type { BudgetForecastUser, BudgetForecastVersion } from 'lumen/models';
import { DEFAULT_ENTRY_TITLE_TRANSLATIONS } from 'lumen/utils/constants/budget-forecast-constants';

export function compareEntries(
  a: BudgetForecastEntryModel,
  b: BudgetForecastEntryModel,
): number {
  if (a.isMain) {
    return -1;
  }
  if (b.isMain) {
    return 1;
  }

  return a.titleWithDefault.localeCompare(b.titleWithDefault);
}

export default class BudgetForecastEntryModel extends Model {
  [Type] = 'budget-forecast/entry' as const;

  @belongsTo('budget-forecast/user', { async: false, inverse: null })
  declare createdBy: BudgetForecastUser | null;

  @hasMany('budget-forecast/version', { async: false, inverse: 'entry' })
  declare versions: HasMany<BudgetForecastVersion>;

  @attr('boolean') declare canEdit: boolean;
  @attr('dayjs') declare createdAt: DayjsTransformReturn | null;
  @attr('string') declare dataType: 'budget' | 'forecast';
  @attr('boolean') declare isArchived: boolean;
  @attr('boolean') declare isLocked: boolean;
  @attr('boolean') declare isMain: boolean;
  @attr('dayjs') declare lastEditedAt: DayjsTransformReturn | null;
  @attr('string') declare title: string | null;

  @service declare intl: Services['intl'];

  get createdAtFromNow(): string {
    if (!this.createdAt) {
      return this.intl.t('general.missing');
    }

    return this.createdAt.fromNow();
  }

  get lastEditedAtFromNow(): string {
    if (!this.lastEditedAt) {
      return this.intl.t('general.missing');
    }

    return this.lastEditedAt.fromNow();
  }

  /**
   *  A main entry will have a null title, so we provide a default title here.
   */
  get titleWithDefault(): string {
    if (this.title) {
      return this.title;
    }

    return this.intl.t(DEFAULT_ENTRY_TITLE_TRANSLATIONS[this.dataType]);
  }
}
